import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface Countdown {
  id: string;
  titulo: string;
  data_fim: string;
  is_active: boolean;
  texto_urgencia?: string;
  link_cta?: string;
  texto_botao?: string;
}

export default function CountdownBanner() {
  const [countdown, setCountdown] = useState<Countdown | null>(null);
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    // Verificar se o banner foi fechado nesta sessão
    const bannerClosed = sessionStorage.getItem('countdown_banner_closed');
    if (bannerClosed === 'true') {
      setIsClosed(true);
      return;
    }

    fetchCountdown();
  }, []);

  const fetchCountdown = async () => {
    try {
      const { data, error } = await supabase
        .from('ibx_countdown_timers')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // Erro silencioso
      }

      if (data) {
        setCountdown(data);
      }
    } catch (err) {
      // Erro silencioso
    }
  };

  useEffect(() => {
    if (!countdown) return;

    const calculateTimeLeft = () => {
      const difference = new Date(countdown.data_fim).getTime() - new Date().getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        setTimeLeft(null);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleClose = () => {
    setIsClosed(true);
    sessionStorage.setItem('countdown_banner_closed', 'true');
  };

  const handleCTAClick = () => {
    if (countdown?.link_cta) {
      window.REACT_APP_NAVIGATE(countdown.link_cta);
    }
  };

  if (!countdown || !timeLeft || isClosed) return null;

  return (
    <div className="bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500 text-white py-3 px-4 relative overflow-hidden">
      {/* Efeito de brilho animado */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6">
          {/* Ícone de urgência */}
          <div className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-2xl animate-pulse"></i>
            <span className="font-bold text-lg">{countdown.titulo}</span>
          </div>

          {/* Contador regressivo */}
          <div className="flex items-center gap-2">
            <div className="flex gap-2">
              {timeLeft.days > 0 && (
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 min-w-[60px] text-center">
                  <div className="text-xl font-bold">{String(timeLeft.days).padStart(2, '0')}</div>
                  <div className="text-xs opacity-90">dias</div>
                </div>
              )}
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 min-w-[60px] text-center">
                <div className="text-xl font-bold">{String(timeLeft.hours).padStart(2, '0')}</div>
                <div className="text-xs opacity-90">horas</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 min-w-[60px] text-center">
                <div className="text-xl font-bold">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <div className="text-xs opacity-90">min</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 min-w-[60px] text-center">
                <div className="text-xl font-bold">{String(timeLeft.seconds).padStart(2, '0')}</div>
                <div className="text-xs opacity-90">seg</div>
              </div>
            </div>
          </div>

          {/* Texto de urgência */}
          {countdown.texto_urgencia && (
            <div className="text-center md:text-left">
              <p className="text-sm md:text-base font-medium">{countdown.texto_urgencia}</p>
            </div>
          )}

          {/* Botão de ação */}
          {countdown.link_cta && (
            <button
              onClick={handleCTAClick}
              className="bg-white text-teal-600 px-6 py-2 rounded-full font-bold text-sm hover:bg-teal-50 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
            >
              {countdown.texto_botao || 'Solicitar agora'}
            </button>
          )}
        </div>
      </div>

      {/* Botão de fechar */}
      <button
        onClick={handleClose}
        className="absolute top-1/2 -translate-y-1/2 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors duration-200 group"
        aria-label="Fechar banner"
      >
        <i className="ri-close-line text-xl group-hover:scale-110 transition-transform"></i>
      </button>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
      `}</style>
    </div>
  );
}
